import { Injectable } from '@angular/core';
import { environment } from '../../../../../../environments/environment';
import posthog from 'posthog-js';

@Injectable({
  providedIn: 'root'
})
export class PostHogService {
  private client: typeof posthog | null = null;
  private appName: string = '';
  private isInitialized: boolean = false;

  constructor() {}

  /**
   * Initialize PostHog with app-specific configuration
   * @param appName - The name of the app (e.g., 'f-user', 'f-admin')
   */
  initialize(appName: string): void {
    if (this.isInitialized) {
      console.warn('PostHog is already initialized');
      return;
    }

    this.appName = appName;

    const apiKey = environment.POSTHOG_API_KEY;
    const apiHost = environment.POSTHOG_HOST;

    if (!apiKey) {
      console.warn('PostHog API key not configured. Error tracking will be disabled.');
      return;
    }

    try {
      posthog.init(apiKey, {
        api_host: apiHost,
        defaults: '2026-01-30',
        autocapture: false,
        // 'history_change' captures the initial pageview AND subsequent SPA
        // navigations (Angular Router drives the History API). Plain `true`
        // only captures the landing pageview.
        capture_pageview: 'history_change',
        disable_session_recording: false,
        capture_exceptions: false
      });

      this.client = posthog;
      this.isInitialized = true;
      // console.log(`PostHog initialized for app: ${appName}`);
    } catch (error) {
      // console.error('Failed to initialize PostHog:', error);
    }
  }

  /**
   * Identify a user in PostHog
   * @param userId - The user's unique identifier
   * @param properties - Additional user properties
   */
  identify(userId: string, properties?: Record<string, any>): void {
    if (!this.client || !this.isInitialized) {
      return;
    }

    try {
      const userProperties = {
        ...properties,
        app_name: this.appName,
        environment: environment.env
      };

      this.client.identify(userId, userProperties);
      // console.log(`PostHog user identified: ${userId}`);
    } catch (error) {
      // console.error('Failed to identify user in PostHog:', error);
    }
  }

  /**
   * Reset user identification (call on logout).
   *
   * IMPORTANT: posthog.reset() also tears down the PostHog Conversations
   * extension, which cannot be re-armed without a fresh posthog.init(). The
   * logout flow (AuthService.signOut) therefore does a full page load rather
   * than an SPA navigation — see the comment there. Do not call reset() from an
   * SPA-navigation path if in-app support chat needs to keep working.
   */
  reset(): void {
    if (!this.client || !this.isInitialized) {
      return;
    }

    try {
      this.client.reset();
    } catch (error) {
      // console.error('Failed to reset PostHog user:', error);
    }
  }

  /**
   * HMAC-verified identity for PostHog Conversations (Support).
   * Distinct from identify(): identify() is analytics, this proves ticket
   * ownership so tickets follow the person across browsers.
   * @param distinctId - the verified user id (must match identify()'s id)
   * @param hash - server-computed HMAC-SHA256 of distinctId
   */
  setVerifiedIdentity(distinctId: string, hash: string): void {
    if (!this.client || !this.isInitialized) return;
    if (!hash) return; // empty hash => backend not configured => stay anonymous
    try {
      this.client.setIdentity(distinctId, hash);
    } catch (error) {
      // non-fatal: chat falls back to anonymous-session tickets
    }
  }

  /** Clear HMAC identity verification (call on logout, before reset()). */
  clearVerifiedIdentity(): void {
    if (!this.client || !this.isInitialized) return;
    try {
      this.client.clearIdentity();
    } catch (error) {
      // non-fatal
    }
  }

  /**
   * Capture an exception/error in PostHog
   * @param error - The error object
   * @param properties - Additional error properties
   */
  captureException(error: any, properties: Record<string, any> = {}): void {
    if (!this.client || !this.isInitialized) {
      return;
    }

    try {
      const errorProperties = {
        ...properties,
        app_name: this.appName,
        environment: environment.env,
        error_message: error?.message || 'Unknown error',
        error_name: error?.name || 'Error',
        error_stack: error?.stack || '',
        error_type: error?.constructor?.name || 'Unknown'
      };

      this.client.captureException(error, errorProperties);
      // console.log('PostHog exception captured:', errorProperties);
    } catch (captureError) {
      // console.error('Failed to capture exception in PostHog:', captureError);
    }
  }

    /**
   * Capture an event in PostHog
   * @param event - The event name
   * @param properties - Additional error properties
   */
    capture(event: string, properties: Record<string, any> = {}): void {
      if (!this.client || !this.isInitialized) {
        return;
      }
  
      try {
        const eventProperties = {
          ...properties,
          app_name: this.appName,
          environment: environment.env,
        };
  
        this.client.capture(event, eventProperties);
        // console.log('PostHog event captured:', eventProperties);
      } catch (captureError) {
        // console.error('Failed to capture event in PostHog:', captureError);
      }
    }

    setPersonProperties(properties: Record<string, any>): void {
      if (!this.client || !this.isInitialized) {
        return;
      }
  
      try {
        const personProperties = {
          ...properties,
          app_name: this.appName,
          environment: environment.env,
        };
  
        this.client.setPersonProperties(personProperties);
        // console.log('PostHog event captured:', personProperties);
      } catch (captureError) {
        // console.error('Failed to capture event in PostHog:', captureError);
      }
    }
  /**
   * Check if PostHog is initialized and ready
   */
  isReady(): boolean {
    return this.isInitialized && this.client !== null;
  }

  /**
   * Get the current app name
   */
  getAppName(): string {
    return this.appName;
  }
}
